import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ParkingVehicleTypeResponse } from '../../../models/vehicle.model';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  CityParkingVehiclePickerDto,
  ParkingCardDetailDto,
  ParkingCardMarketingDashboardDto,
  ParkingCardStatusDashboardDto,
  ParkingCardTableDto,
  ParkingCardTableQuery,
  SaveParkingCardPayload,
  SaveAdminParkingMemberPayload,
  SubscriptionInfoDto,
  ParkingCardRenewalRow,
  IdNameOption,
  VerifyParkingCardUserDto,
  ImportParkingCardsPayload,
  ImportParkingCardsResponse,
  DeleteParkingCardPayload,
  LookupParkingCardsByLicensesDto,
} from '../../../models/parking/parking-card-list.model';

@Injectable({
  providedIn: 'root',
})
export class ApiParkingCardListService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  getParkingCardForTable(query: ParkingCardTableQuery): Observable<ParkingCardTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.sortColumn) params['sortColumn'] = query.sortColumn;
    if (query.sortDirection) params['sortDirection'] = query.sortDirection;
    if (query.status && query.status !== 'all') params['status'] = query.status;
    if (query.clientId && query.clientId > 0) params['clientId'] = query.clientId;
    if (query.cardMode != null && query.cardMode > 0) params['cardMode'] = query.cardMode;
    if (query.vehicleTypeId != null && query.vehicleTypeId > 0) params['vehicleTypeId'] = query.vehicleTypeId;

    return this.apiMethodService.request<ParkingCardTableDto>(
      '/backoffice/getParkingCardForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  /** บัตรที่ยกเลิกแล้ว — parking_card.deleted = true (ดูอย่างเดียว) */
  getDeletedParkingCardForTable(query: ParkingCardTableQuery): Observable<ParkingCardTableDto> {
    const token = this.cookieService.get('token');
    const params: Record<string, string | number> = {
      siteId: query.siteId,
      userId: query.userId,
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };

    if (query.searchField) params['searchField'] = query.searchField;
    if (query.searchText) params['searchText'] = query.searchText;
    if (query.sortColumn) params['sortColumn'] = query.sortColumn;
    if (query.sortDirection) params['sortDirection'] = query.sortDirection;

    return this.apiMethodService.request<ParkingCardTableDto>(
      '/backoffice/getDeletedParkingCardForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getDeletedParkingCardForTable',
        token,
        headerOnly: false,
        params,
      }
    );
  }

  getParkingVehicleTypes(): Observable<ParkingVehicleTypeResponse[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingVehicleTypeResponse[]>(
      '/backoffice/GetParkingVehicleType',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: false,
        labelLog: 'GetParkingVehicleType',
        token,
        headerOnly: false,
      }
    );
  }

  getParkingCardMarketingDashboard(siteId: number, userId: number): Observable<ParkingCardMarketingDashboardDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingCardMarketingDashboardDto>(
      '/backoffice/getParkingCardMarketingDashboard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardMarketingDashboard',
        token,
        headerOnly: false,
        params: { siteId, userId },
      }
    );
  }

  getParkingCardStatusDashboard(siteId: number, userId: number): Observable<ParkingCardStatusDashboardDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingCardStatusDashboardDto>(
      '/backoffice/getParkingCardStatusDashboard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardStatusDashboard',
        token,
        headerOnly: false,
        params: { siteId, userId },
      }
    );
  }

  verifyParkingCardUser(
    searchType: 'phone' | 'lineid' | 'uid' | 'cpuserid',
    searchText: string,
    siteId: number,
    userId: number
  ): Observable<VerifyParkingCardUserDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<VerifyParkingCardUserDto>(
      '/backoffice/verifyParkingCardUser',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'verifyParkingCardUser',
        token,
        headerOnly: false,
        params: { searchType, searchText, siteId, userId },
      }
    );
  }

  getParkingCardById(cardId: number, siteId: number, userId: number): Observable<ParkingCardDetailDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingCardDetailDto>(
      '/backoffice/getParkingCardById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardById',
        token,
        headerOnly: false,
        params: { cardId, siteId, userId },
      }
    );
  }

  getCityParkingVehiclesByLineIdForCard(
    lineId: string,
    siteId: number,
    userId: number
  ): Observable<CityParkingVehiclePickerDto[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<CityParkingVehiclePickerDto[]>(
      '/backoffice/getCityParkingVehiclesByLineIdForCard',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: false,
        labelLog: 'getCityParkingVehiclesByLineIdForCard',
        token,
        headerOnly: false,
        params: { lineId, siteId, userId },
      }
    );
  }

  getParkingCardTypes(): Observable<Array<{ id: number; name: string }>> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<Array<{ id: number; name: string }>>(
      '/backoffice/GetCardType',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetCardType',
        token,
        headerOnly: false,
      }
    );
  }

  saveParkingCard(payload: SaveParkingCardPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/saveParkingCard',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveParkingCard',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getParkingProvinces(): Observable<IdNameOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<IdNameOption[]>(
      '/backoffice/getParkingProvinces',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: false,
        labelLog: 'getParkingProvinces',
        token,
        headerOnly: false,
      }
    );
  }

  saveParkingCardMember(payload: SaveAdminParkingMemberPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/saveParkingCardMember',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveParkingCardMember',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getParkingCardRenewals(cardId: number, siteId: number, userId: number): Observable<ParkingCardRenewalRow[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ParkingCardRenewalRow[]>(
      '/backoffice/getParkingCardRenewals',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardRenewals',
        token,
        headerOnly: false,
        params: { cardId, siteId, userId },
      }
    );
  }

  getAllParkingSubscriptionsBySite(siteId: number): Observable<IdNameOption[]> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<IdNameOption[]>(
      '/backoffice/GetParkingSubscriptionsBySite',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetParkingSubscriptionsBySite',
        token,
        headerOnly: false,
        params: { siteId },
      }
    );
  }

  getParkingSubscriptionDropdown(siteId: number, selectedId = 0): Observable<{ data: IdNameOption[]; more: boolean }> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<{ data: IdNameOption[]; more: boolean }>(
      '/backoffice/GetParkingSubscriptionDropdown',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'GetParkingSubscriptionDropdown',
        token,
        headerOnly: false,
        params: { siteId, selectedId },
      }
    );
  }

  getSubscriptionInfoById(subscriptionId: number): Observable<SubscriptionInfoDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<SubscriptionInfoDto>(
      '/backoffice/getSubscriptionInfoById',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getSubscriptionInfoById',
        token,
        headerOnly: false,
        params: { subscriptionId },
      }
    );
  }

  bulkUpdateParkingCardDates(payload: {
    userId: number;
    menuId: number;
    siteId: number;
    cardIds: number[];
    issueDate: string;
    expireDate: string;
  }): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/bulkUpdateParkingCardDates',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'bulkUpdateParkingCardDates',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  lookupParkingCardsByLicenses(payload: {
    siteId: number;
    userId: number;
    licenses?: string[];
    items?: Array<{
      license: string;
      vehicleTypeId?: number | null;
      vehicleTypeName?: string | null;
    }>;
  }): Observable<LookupParkingCardsByLicensesDto> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<LookupParkingCardsByLicensesDto>(
      '/backoffice/lookupParkingCardsByLicenses',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'lookupParkingCardsByLicenses',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  bulkUpdateParkingCardSubscription(payload: {
    userId: number;
    menuId: number;
    siteId: number;
    cardIds: number[];
    subscriptionId: number;
  }): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/bulkUpdateParkingCardSubscription',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'bulkUpdateParkingCardSubscription',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  bulkUpdateParkingCards(payload: {
    userId: number;
    menuId: number;
    siteId: number;
    cardIds: number[];
    updateDates: boolean;
    issueDate?: string | null;
    expireDate?: string | null;
    updateSubscription: boolean;
    subscriptionId?: number;
    updateClient: boolean;
    clientId?: number;
  }): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/bulkUpdateParkingCards',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'bulkUpdateParkingCards',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  importParkingCards(payload: ImportParkingCardsPayload): Observable<ImportParkingCardsResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ImportParkingCardsResponse>(
      '/backoffice/importParkingCards',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'importParkingCards',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getParkingCardTaiwanSyncStatus(cardId: number): Observable<unknown> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<unknown>(
      '/backoffice/getParkingCardTaiwanSyncStatus',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardTaiwanSyncStatus',
        token,
        headerOnly: false,
        params: { cardId },
      }
    );
  }

  getParkingCardTaiwanSyncLog(cardId: number, page = 1, pageSize = 20): Observable<unknown> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<unknown>(
      '/backoffice/getParkingCardTaiwanSyncLog',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardTaiwanSyncLog',
        token,
        headerOnly: false,
        params: { cardId, page, pageSize },
      }
    );
  }

  resyncParkingCardTaiwan(payload: {
    userId: number;
    menuId: number;
    siteId: number;
    cardCode: string;
  }): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/resyncParkingCardTaiwan',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'resyncParkingCardTaiwan',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  getParkingCardsPendingTaiwanSyncForTable(params: {
    siteId: number;
    page?: number;
    pageSize?: number;
    searchText?: string;
  }): Observable<unknown> {
    const token = this.cookieService.get('token');
    const query: Record<string, string | number> = {
      siteId: params.siteId,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
    if (params.searchText) query['searchText'] = params.searchText;
    return this.apiMethodService.request<unknown>(
      '/backoffice/getParkingCardsPendingTaiwanSyncForTable',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getParkingCardsPendingTaiwanSyncForTable',
        token,
        headerOnly: false,
        params: query,
      }
    );
  }

  bulkSendParkingCardsToTaiwan(payload: {
    userId: number;
    menuId: number;
    siteId: number;
    cardIds: number[];
  }): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/bulkSendParkingCardsToTaiwan',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'bulkSendParkingCardsToTaiwan',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deleteParkingCard(payload: DeleteParkingCardPayload): Observable<ActionBackOfficeResponse> {
    const token = this.cookieService.get('token');
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/backoffice/deleteParkingCard',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteParkingCard',
        token,
        headerOnly: false,
        body: payload,
      }
    );
  }
}
